import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import metadataService from '../../services/metadataService';
import {
  Home,
  DollarSign,
  Users,
  Clock,
  Shield,
  Zap,
  CheckCircle,
  ArrowLeft,
  Phone,
  Mail,
  MapPin,
  Twitter,
  Linkedin,
  Facebook,
  Instagram,
  Building,
  Key,
  FileText,
  ChevronRight,
} from 'lucide-react';

const PropertyManagement = () => {
  const benefits = [
    {
      icon: DollarSign,
      title: 'Maximize Rental Income in Kenya',
      description: 'Reduce vacancies with AI-powered tenant matching and collect rent on time with secure payment systems tailored for Kenyan landlords.',
    },
    {
      icon: Clock,
      title: 'Save Time with Automation',
      description: 'Automate rent collection, maintenance requests, and tenant communication with our user-friendly platform designed for property management in Kenya.',
    },
    {
      icon: Shield,
      title: 'Protect Your Property Investment',
      description: 'Our rigorous tenant screening and compliance with Kenya’s rental laws ensure your property investment is secure.',
    },
    {
      icon: Users,
      title: 'Hassle-Free Tenant Management',
      description: 'From screening to lease agreements, we handle all tenant interactions for landlords in Nairobi, Mombasa, and beyond.',
    },
  ];

  const painPoints = [
    {
      title: 'Vacancies Drain Your Income',
      description: 'Empty properties in Kenya mean lost rent. Our platform markets your listings to qualified tenants across Kenya, filling vacancies faster.',
    },
    {
      title: 'Unpaid Rent and Bad Tenants',
      description: 'Late payments or problematic tenants can cost you thousands. Our tenant screening and automated collections in Kenya minimize risks.',
    },
    {
      title: 'Time-Consuming Manual Processes',
      description: 'Managing leases, maintenance, and inquiries manually is exhausting. Rent Utopia automates these tasks for Kenyan property owners.',
    },
    {
      title: 'Legal and Compliance Risks',
      description: 'Navigating Kenya’s complex rental laws is challenging. Our platform ensures compliance, avoiding costly legal issues for landlords.',
    },
  ];

  const features = [
    {
      icon: Key,
      title: 'AI-Powered Tenant Matching in Kenya',
      description: 'Our advanced algorithms connect your property with reliable tenants across Kenya, quickly.',
    },
    {
      icon: DollarSign,
      title: 'Secure Rent Collection',
      description: 'Automated, bank-level secure payment processing ensures timely rent payments for Kenyan landlords.',
    },
    {
      icon: FileText,
      title: 'Digital Lease Management',
      description: 'Create, sign, and store lease agreements digitally with ease for property management in Kenya.',
    },
    {
      icon: Building,
      title: 'Maintenance Coordination',
      description: 'Streamlined maintenance requests with real-time tracking and updates for Kenyan rental properties.',
    },
  ];

  const defaultFaqs = [
    {
      question: 'Why do landlords in Kenya need a property management system?',
      answer: 'A property management system like Rent Utopia simplifies landlord tasks by automating rent collection, tenant screening, and maintenance coordination in Kenya. It reduces vacancies, ensures legal compliance with Kenyan laws, and maximizes rental income, saving you time and money.',
    },
    {
      question: 'How does Rent Utopia reduce rental property vacancies in Kenya?',
      answer: 'Rent Utopia uses AI-powered tenant matching to connect your property with qualified tenants across Kenya, including Nairobi, Mombasa, and Kisumu. Our platform markets your listings effectively, reducing vacancy periods.',
    },
    {
      question: 'What makes Rent Utopia different from other property agencies in Kenya?',
      answer: 'Unlike traditional agencies, Rent Utopia leverages cutting-edge AI technology and secure payment systems to provide a seamless, transparent experience for property management in Kenya. We offer end-to-end services, from tenant screening to maintenance.',
    },
    {
      question: 'How can Rent Utopia save money for landlords in Kenya?',
      answer: 'Rent Utopia minimizes financial losses by reducing vacancies, ensuring timely rent payments, and preventing costly tenant issues through rigorous screening. Our automation cuts administrative costs for Kenyan property owners.',
    },
  ];

  const [faqs, setFaqs] = useState(defaultFaqs);

  useEffect(() => {
    metadataService.getFaqs()
      .then((data) => {
        if (data.length > 0) {
          setFaqs(data);
        }
      })
      .catch((error) => console.error('Error fetching FAQs:', error));
  }, []);

  return (
    <div className="min-h-screen bg-karibu-100">
      <div style={{ paddingTop: '5rem' }}></div>

      <Helmet>
        <title>Property Management Services Kenya | Rent Utopia</title>
        <meta
          name="description"
          content="Discover Rent Utopia's property management services in Kenya. Maximize rental income, reduce vacancies, and simplify landlord tasks with our AI-powered platform. Serving Nairobi, Mombasa, Kisumu, and beyond."
        />
        <meta
          name="keywords"
          content="property management Kenya, rental management Nairobi, apartment management Kenya, landlord services Nairobi, property agency Kenya, real estate management Mombasa, tenant screening Kenya, rental properties Kisumu"
        />
        <meta name="robots" content="index, follow" />
        <meta name="author" content="Rent Utopia" />
        <meta name="viewport" content="width=device-width, initial-scale=1.0" />
        <link rel="canonical" href="https://utopia.pegasus-pms.co.ke/property-management" />
        <meta name="language" content="en" />
        <meta http-equiv="content-language" content="en" />
        <meta property="og:title" content="Property Management Services Kenya | Rent Utopia" />
        <meta
          property="og:description"
          content="Maximize rental income and simplify property management in Kenya with Rent Utopia's AI-powered platform. Serving landlords nationwide."
        />
        <meta property="og:url" content="https://utopia.pegasus-pms.co.ke/property-management" />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://utopia.pegasus-pms.co.ke/images/property-management-hero.jpg" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Property Management Services Kenya | Rent Utopia" />
        <meta
          name="twitter:description"
          content="Simplify property management in Kenya with Rent Utopia. AI-powered tenant matching, secure rent collection, and more for landlords in Nairobi and beyond."
        />
        <meta name="twitter:image" content="https://utopia.pegasus-pms.co.ke/images/property-management-hero.jpg" />
        <meta name="twitter:site" content="@ITOramaKE" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Service",
            "serviceType": "Property Management",
            "provider": {
              "@type": "Organization",
              "name": "Rent Utopia",
              "url": "https://utopia.pegasus-pms.co.ke",
              "sameAs": [
                "https://twitter.com/ITOramaKE",
                "https://www.linkedin.com/company/it-orama",
                "https://www.facebook.com/ITOramaKE",
                "https://www.instagram.com/ITOramaKE",
              ],
              "address": {
                "@type": "PostalAddress",
                "streetAddress": "Westlands",
                "addressLocality": "Nairobi",
                "addressRegion": "Nairobi County",
                "addressCountry": "KE",
              },
              "contactPoint": {
                "@type": "ContactPoint",
                "telephone": "+254-712-292-899",
                "contactType": "Customer Service",
                "email": "itorama254@gmail.com",
                "availableLanguage": ["English", "Swahili"],
              },
            },
            "areaServed": { "@type": "Country", "name": "Kenya" },
            "priceRange": "$$",
            "description": "Rent Utopia offers professional property management services across Kenya, including tenant screening, rent collection, and maintenance coordination for landlords nationwide.",
          })}
        </script>
      </Helmet>

      {/* Hero Section */}
      <div className="relative bg-highland-700 text-karibu-100 overflow-hidden">
        <div
          aria-hidden="true"
          className="absolute top-0 left-1/2 -translate-x-1/2 w-[700px] h-[420px] rounded-arch bg-gradient-to-b from-dawn-500/20 via-highland-500/10 to-transparent blur-2xl"
        />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="text-center">
            <div className="flex items-center justify-center mb-6">
              <div className="p-4 bg-white/10 rounded-full">
                <Building className="w-12 h-12" />
              </div>
            </div>
            <h1 className="font-display text-5xl font-bold mb-6">Property Management Services in Kenya</h1>
            <p className="text-xl text-karibu-200/80 max-w-3xl mx-auto leading-relaxed">
              Maximize your rental income and simplify property management with Rent Utopia’s AI-powered platform.
              Serving landlords across Nairobi, Mombasa, Kisumu, and beyond.
            </p>
            <a
              href="https://pegasus-pms.co.ke/signup"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center px-6 py-3 mt-6 bg-karibu-100 text-highland-700 font-semibold rounded-xl hover:bg-white transition-colors duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
              aria-label="Get started with Rent Utopia property management"
            >
              Get Started <ChevronRight className="w-5 h-5 ml-2" />
            </a>
          </div>
        </div>
      </div>

      {/* Navigation */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
        <div className="flex items-center space-x-2 text-sm text-soil-600">
          <button
            onClick={() => window.history.back()}
            className="flex items-center hover:text-highland-500 transition-colors"
            aria-label="Go back"
          >
            <ArrowLeft className="w-4 h-4 mr-1" />
            Back
          </button>
          <span>/</span>
          <a
            href="/"
            className="flex items-center hover:text-highland-500 transition-colors"
            aria-label="Go to home"
          >
            <Home className="w-4 h-4 mr-1" />
            Home
          </a>
          <span>/</span>
          <a
            href="/property-management"
            className="flex items-center hover:text-highland-500 transition-colors"
            aria-label="Go to property management"
          >
            Property Management
          </a>
        </div>
      </div>

      {/* Why Landlords Need Property Management */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="text-center mb-12">
          <h2 className="font-display text-3xl font-bold text-soil-900 mb-4">
            Why Landlords in Kenya Need Rent Utopia Property Management
          </h2>
          <p className="text-soil-600 max-w-2xl mx-auto">
            Managing rental properties in Kenya can be challenging. Rent Utopia’s property management system helps
            landlords save time, reduce costs, and maximize returns in Nairobi, Mombasa, and beyond.
          </p>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {benefits.map((benefit, index) => (
            <div
              key={index}
              className="bg-white rounded-xl shadow-sm border border-soil-100 p-6 text-center"
            >
              <benefit.icon className="w-8 h-8 text-highland-500 mx-auto mb-3" />
              <h3 className="text-lg font-semibold text-soil-900 mb-2">{benefit.title}</h3>
              <p className="text-soil-600 text-sm">{benefit.description}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Pain Points: How Landlords Are Losing Money */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 bg-soil-50">
        <div className="bg-white rounded-xl shadow-sm border border-soil-100 p-8">
          <h2 className="font-display text-3xl font-bold text-soil-900 mb-8 text-center">
            How You’re Losing Money Without Property Management in Kenya
          </h2>
          <div className="grid md:grid-cols-2 gap-6">
            {painPoints.map((pain, index) => (
              <div key={index} className="p-6 bg-soil-50 rounded-lg">
                <h3 className="font-semibold text-soil-900 mb-2">{pain.title}</h3>
                <p className="text-soil-600 text-sm">{pain.description}</p>
              </div>
            ))}
          </div>
          <div className="text-center mt-8">
            <a
              href="https://pegasus-pms.co.ke/signup"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center px-6 py-3 bg-highland-500 text-white font-semibold rounded-xl hover:bg-highland-600 transition-colors duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
              aria-label="Solve these problems with Rent Utopia"
            >
              Solve These Problems with Rent Utopia <ChevronRight className="w-5 h-5 ml-2" />
            </a>
          </div>
        </div>
      </div>

      {/* How Rent Utopia Makes Work Easier */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="text-center mb-12">
          <h2 className="font-display text-3xl font-bold text-soil-900 mb-4">
            How Rent Utopia Simplifies Property Management in Kenya
          </h2>
          <p className="text-soil-600 max-w-2xl mx-auto">
            Our platform leverages cutting-edge technology to streamline every aspect of property management,
            from tenant screening to maintenance in Kenya.
          </p>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {features.map((feature, index) => (
            <div key={index} className="bg-white rounded-xl shadow-sm border border-soil-100 p-6">
              <div className="flex items-start space-x-3">
                <feature.icon className="w-6 h-6 text-highland-500 mt-1" />
                <div>
                  <h3 className="font-semibold text-soil-900 mb-1">{feature.title}</h3>
                  <p className="text-soil-600 text-sm">{feature.description}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Testimonials */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 bg-soil-50">
        <div className="text-center mb-12">
          <h2 className="font-display text-3xl font-bold text-soil-900 mb-4">
            What Landlords in Kenya Say About Rent Utopia
          </h2>
          <p className="text-soil-600 max-w-2xl mx-auto">
            Hear from property owners across Kenya who trust Rent Utopia to manage their rentals.
          </p>
        </div>
        <div className="grid md:grid-cols-2 gap-6">
          {[
            {
              name: 'Jane Wambui, Nairobi',
              quote: 'Rent Utopia reduced my vacancy periods by 50% and handles all tenant issues seamlessly. Highly recommend for property management in Kenya!',
              image: '/images/testimonial-jane.jpg',
            },
            {
              name: 'Peter Omondi, Mombasa',
              quote: 'The automated rent collection and maintenance tracking have saved me countless hours. Rent Utopia is a game-changer for Kenyan landlords.',
              image: '/images/testimonial-peter.jpg',
            },
          ].map((testimonial, index) => (
            <div key={index} className="bg-white rounded-xl shadow-sm border border-soil-100 p-6">
              <img
                src={testimonial.image}
                alt={`Testimonial from ${testimonial.name} about Rent Utopia property management in Kenya`}
                loading="lazy"
                className="w-16 h-16 rounded-full mx-auto mb-4 object-cover"
              />
              <p className="text-soil-600 text-sm italic mb-4">"{testimonial.quote}"</p>
              <p className="text-highland-500 font-medium">{testimonial.name}</p>
            </div>
          ))}
        </div>
      </div>

      {/* FAQ Section */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="text-center mb-12">
          <h2 className="font-display text-3xl font-bold text-soil-900 mb-4">
            Frequently Asked Questions About Property Management in Kenya
          </h2>
          <p className="text-soil-600 max-w-2xl mx-auto">
            Got questions about property management in Kenya? We have answers for landlords nationwide.
          </p>
        </div>
        <div className="space-y-6">
          {faqs.map((faq, index) => (
            <div key={index} className="bg-white rounded-xl shadow-sm border border-soil-100 p-6">
              <h3 className="text-lg font-semibold text-soil-900 mb-2">{faq.question}</h3>
              <p className="text-soil-600 text-sm">{faq.answer}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Call to Action */}
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 bg-highland-700 text-karibu-100 overflow-hidden">
        <div
          aria-hidden="true"
          className="absolute top-0 left-1/2 -translate-x-1/2 w-[500px] h-[280px] rounded-arch bg-gradient-to-b from-dawn-500/20 to-transparent blur-2xl"
        />
        <div className="relative text-center">
          <h2 className="font-display text-3xl font-bold mb-4">Ready to Simplify Property Management in Kenya?</h2>
          <p className="text-karibu-200/80 max-w-2xl mx-auto mb-6">
            Join thousands of landlords across Kenya who trust Rent Utopia to manage their rental properties.
            Get started today!
          </p>
          <a
            href="https://pegasus-pms.co.ke/signup"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center px-6 py-3 bg-karibu-100 text-highland-700 font-semibold rounded-xl hover:bg-white transition-colors duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
            aria-label="Start managing your properties with Rent Utopia in Kenya"
          >
            Get Started Now <ChevronRight className="w-5 h-5 ml-2" />
          </a>
        </div>
      </div>
    </div>
  );
};

export default PropertyManagement;
